import * as React from 'react'
import styled from 'styled-components'
import ViewLayout from './ViewLayout'

const bemComponentName = 'centeredLayout'

export const classes = {
  wrapper: `${bemComponentName}__wrapper`,
  illustration: `${bemComponentName}__illustration`,
  title: `${bemComponentName}__title`,
  subtitle: `${bemComponentName}__subtitle`,
  content: `${bemComponentName}__content`,
}

/**
 * Shared centered layout used for sparse/informational screens (errors, session
 * timeout, access denied). Keeps a compact, intentionally centered card instead
 * of a half-empty two-panel split. Extends `ViewLayout` so the shared
 * `viewLayout__*` styles (e.g. the cta button) keep applying.
 */
const StyledCentered = styled(ViewLayout)`
  .${classes.wrapper} {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    row-gap: 1rem;
    padding: 2rem 1.5rem;
    max-width: 460px;
    margin: 0 auto;
  }

  .${classes.illustration} {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.5rem;

    svg,
    img {
      width: auto;
      max-width: 220px;
      height: auto;
      max-height: 150px;
    }
  }

  .${classes.title} {
    margin: 0;
    font-weight: 500;
    font-size: 24px;
    line-height: 28px;
    color: #000;
  }

  .${classes.subtitle} {
    margin: 0;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: #666;
  }

  .${classes.content} {
    display: flex;
    flex-direction: column;
    align-items: center;
    row-gap: 1rem;
    width: 100%;
  }

  ${(props): string => props.theme.desktopBreakpoint} {
    .${classes.wrapper} {
      padding: 3rem 1.5rem;
    }
  }
`

type CenteredViewLayoutProps = {
  illustration?: React.ReactNode
  title?: React.ReactNode
  subtitle?: React.ReactNode
  children?: React.ReactNode
  className?: string
  style?: React.CSSProperties
}

const CenteredViewLayout = ({
  illustration,
  title,
  subtitle,
  children,
  className,
  style,
}: CenteredViewLayoutProps): JSX.Element => (
  <StyledCentered className={className} style={style}>
    <div className={classes.wrapper}>
      {illustration && <div className={classes.illustration}>{illustration}</div>}
      {title && <h1 className={classes.title}>{title}</h1>}
      {subtitle && <p className={classes.subtitle}>{subtitle}</p>}
      {children && <div className={classes.content}>{children}</div>}
    </div>
  </StyledCentered>
)

CenteredViewLayout.defaultProps = {
  illustration: null,
  title: null,
  subtitle: null,
  children: null,
  className: undefined,
  style: undefined,
}

export default CenteredViewLayout
